import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FrontendDonor } from '@/services/transform';

interface PartnerDetailModalProps {
  partner: FrontendDonor | null;
  onClose: () => void;
}

const MOCK_PROJECTS: Record<string, { nameZh: string; nameEn: string; yearRange: string; status: 'active' | 'completed' }[]> = {
  default: [
    { nameZh: '联合科创圆桌论坛', nameEn: 'Joint Innovation Roundtable Forum', yearRange: '2023–2026', status: 'active' },
    { nameZh: '青年科学家交流计划', nameEn: 'Young Scientist Exchange Program', yearRange: '2022–2025', status: 'completed' },
    { nameZh: '前沿技术研究合作', nameEn: 'Frontier Technology Research Collaboration', yearRange: '2024–2027', status: 'active' },
  ],
};

const MOCK_STATS: Record<string, { years: number; projects: number; experts: number }> = {
  default: { years: 3, projects: 8, experts: 42 },
  mit: { years: 5, projects: 14, experts: 68 },
  pku: { years: 5, projects: 12, experts: 55 },
  eth: { years: 4, projects: 9, experts: 38 },
  cas: { years: 5, projects: 18, experts: 90 },
  mps: { years: 3, projects: 7, experts: 30 },
  huawei: { years: 4, projects: 11, experts: 60 },
  siemens: { years: 3, projects: 6, experts: 28 },
  alibaba: { years: 4, projects: 10, experts: 50 },
};

export default function PartnerDetailModal({ partner, onClose }: PartnerDetailModalProps) {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  useEffect(() => {
    if (!partner) return;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [partner, onClose]);

  if (!partner) return null;

  const stats = MOCK_STATS['default'];
  const projects = MOCK_PROJECTS['default'];

  const typeColorMap: Record<string, string> = {
    university: 'bg-[#2C7DA0]/10 text-[#2C7DA0]',
    research: 'bg-emerald-100 text-emerald-700',
    enterprise: 'bg-amber-100 text-amber-700',
    nonprofit: 'bg-purple-100 text-purple-700',
    partner: 'bg-sky-100 text-sky-700',
    organization: 'bg-amber-100 text-amber-700',
    individual: 'bg-purple-100 text-purple-700',
    donor: 'bg-rose-100 text-rose-700',
  };

  const typeLabelMap: Record<string, { zh: string; en: string }> = {
    university: { zh: '高校', en: 'University' },
    research: { zh: '科研机构', en: 'Research Inst.' },
    enterprise: { zh: '企业', en: 'Enterprise' },
    nonprofit: { zh: '非营利', en: 'Non-profit' },
    partner: { zh: '合作伙伴', en: 'Partner' },
    organization: { zh: '机构', en: 'Organization' },
    individual: { zh: '个人', en: 'Individual' },
    donor: { zh: '捐赠者', en: 'Donor' },
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-xl max-h-[88vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-[#0A2540]/8 flex items-start gap-4">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0"
            style={{ backgroundColor: partner.color || '#0A2540' }}
          >
            {partner.abbr && partner.abbr.length > 4 ? partner.abbr.slice(0, 4) : (partner.abbr || 'GT')}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 flex-wrap mb-1">
              <h2 className="font-bold text-[#0A2540] text-lg leading-tight">
                {isZh ? partner.name : partner.nameEn}
              </h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${typeColorMap[partner.type] ?? ''}`}>
                {isZh ? typeLabelMap[partner.type]?.zh : typeLabelMap[partner.type]?.en}
              </span>
            </div>
            <p className="text-[#0A2540]/50 text-sm">{partner.year}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F7F9FC] hover:bg-[#0A2540]/10 text-[#0A2540]/50 hover:text-[#0A2540] transition-all cursor-pointer shrink-0"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: stats.years, labelZh: '合作年限', labelEn: 'Years Together', icon: 'ri-time-line', unit: isZh ? '年' : 'yrs' },
              { value: stats.projects, labelZh: '联合项目', labelEn: 'Joint Projects', icon: 'ri-briefcase-line', unit: '' },
              { value: stats.experts, labelZh: '参与专家', labelEn: 'Experts Involved', icon: 'ri-user-3-line', unit: '+' },
            ].map(({ value, labelZh, labelEn, icon, unit }) => (
              <div key={icon} className="bg-[#F7F9FC] rounded-xl p-4 text-center">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className={`${icon} text-[#2C7DA0] text-lg`} />
                </div>
                <div className="font-bold text-[#0A2540] text-xl tabular-nums">{value}{unit}</div>
                <div className="text-[#0A2540]/40 text-xs mt-0.5">{isZh ? labelZh : labelEn}</div>
              </div>
            ))}
          </div>

          {/* Description */}
          <div>
            <h3 className="text-[#0A2540] font-semibold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-information-line text-[#2C7DA0]" />
              </div>
              {t('partnerModal.detail')}
            </h3>
            <p className="text-[#0A2540]/70 text-sm leading-relaxed">
              {isZh ? partner.description : partner.descriptionEn}
            </p>
          </div>

          {/* Collaboration Domain */}
          <div>
            <h3 className="text-[#0A2540] font-semibold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-focus-3-line text-[#2C7DA0]" />
              </div>
              {t('partnerModal.collab')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {(isZh ? partner.domain : partner.domainEn).split('、').join(',').split(',').map((d) => (
                <span key={d} className="px-3 py-1.5 bg-[#2C7DA0]/10 text-[#2C7DA0] rounded-full text-xs font-medium">
                  {d.trim()}
                </span>
              ))}
            </div>
          </div>

          {/* Joint Projects */}
          <div>
            <h3 className="text-[#0A2540] font-semibold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-git-branch-line text-[#2C7DA0]" />
              </div>
              {t('partnerModal.projects')}
            </h3>
            <div className="space-y-2.5">
              {projects.map((proj) => (
                <div key={proj.nameEn} className="flex items-center gap-3 p-3 bg-[#F7F9FC] rounded-xl">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${proj.status === 'active' ? 'bg-emerald-400' : 'bg-[#0A2540]/20'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0A2540] text-sm font-medium truncate">
                      {isZh ? proj.nameZh : proj.nameEn}
                    </div>
                    <div className="text-[#0A2540]/40 text-xs">{proj.yearRange}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${proj.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-[#0A2540]/8 text-[#0A2540]/50'}`}>
                    {proj.status === 'active' ? (isZh ? '进行中' : 'Active') : (isZh ? '已完成' : 'Done')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#0A2540]/8 flex items-center justify-between">
          <a
            href={partner.website}
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-1.5 text-[#2C7DA0] text-sm font-medium hover:underline cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line" />
            {t('partnerModal.viewSite')}
          </a>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('partnerModal.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
